import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPackage, FiClock, FiCheck, FiTruck, FiAlertCircle } from 'react-icons/fi';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useSelector } from 'react-redux';
import LoadingSpinner from '../Components/LoadingSpinner';

const getStatusIcon = (status) => {
  switch (status) {
    case 'processing':
      return <FiPackage className="w-5 h-5 text-brand-500" />;
    case 'shipped':
      return <FiTruck className="w-5 h-5 text-yellow-500" />;
    case 'delivered':
      return <FiCheck className="w-5 h-5 text-green-500" />;
    default:
      return <FiClock className="w-5 h-5 text-gray-500" />;
  }
};

const getStatusText = (status) => {
  if (status === true) return 'Delivered';
  
  switch (status) {
    case 'processing':
      return 'Processing';
    case 'shipped':
      return 'Shipped';
    case 'delivered':
      return 'Delivered';
    default:
      return 'Pending';
  }
};

// Product image cache
const productCache = {};

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productDetails, setProductDetails] = useState({});
  const { token } = useSelector((state) => state.user);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching orders with token...');
        const response = await axios.get(`${API_BASE_URL}/order`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('Orders response:', response);
        
        if (response.status === 200 && response.data && response.data.data) {
          console.log('Orders data:', response.data.data);
          setOrders(response.data.data || []);
          
          // Get a list of unique product IDs from orders
          const productIds = new Set();
          response.data.data.forEach(order => {
            order.cartItems.forEach(item => {
              if (item.product && item.product._id) {
                productIds.add(item.product._id);
              }
            });
          });
          
          // Fetch product details for all products
          await fetchProductDetails(Array.from(productIds));
        } else {
          console.error('Orders response not successful:', response.data);
          setError('Failed to retrieve orders. Please try again.');
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        console.error('Error response:', err.response?.data);
        console.error('Error status:', err.response?.status);
        
        if (err.response?.status === 404) {
          setError('Orders endpoint not found. Please try again later.');
        } else if (err.response?.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else {
          setError(err.response?.data?.message || 'Failed to fetch orders. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    // Function to fetch product details
    const fetchProductDetails = async (productIds) => {
      try {
        const productData = {};
        
        // Check cache first
        const idsToFetch = productIds.filter(id => !productCache[id]);
        
        // Fetch only uncached products
        if (idsToFetch.length > 0) {
          await Promise.all(
            idsToFetch.map(async (productId) => {
              try {
                const response = await axios.get(`${API_BASE_URL}/products/${productId}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (response.data && response.data.data) {
                  productCache[productId] = response.data.data;
                  productData[productId] = response.data.data;
                }
              } catch (error) {
                console.error(`Failed to fetch product ${productId}:`, error);
              }
            })
          );
        }
        
        // Add cached products
        productIds.forEach(id => {
          if (productCache[id] && !productData[id]) {
            productData[id] = productCache[id];
          }
        });
        
        setProductDetails(prev => ({ ...prev, ...productData }));
      } catch (error) {
        console.error('Error fetching product details:', error);
      }
    };

    if (token) {
      fetchOrders();
    } else {
      console.error('No token available for fetching orders');
      setError('Please log in to view your orders');
      setLoading(false);
    }
  }, [token]);

  // Helper function to get product details
  const getProductDetail = (productId) => {
    return productDetails[productId] || {};
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="pt-24 min-h-screen bg-cream flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="pt-24 min-h-screen bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white p-6 rounded-2xl border border-sand shadow-card">
              <div className="flex items-center justify-center text-red-500 mb-4">
                <FiAlertCircle className="w-8 h-8" />
              </div>
              <p className="text-center text-lg">{error}</p>
              <div className="mt-6 text-center">
                <button 
                  onClick={() => window.location.reload()}
                  className="bg-ink text-cream px-6 py-2 rounded-xl font-medium hover:bg-brand-500 hover:text-ink transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (orders.length === 0) {
    return (
      <>
        <Navbar />
        <div className="pt-24 min-h-screen bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-3xl font-display font-normal tracking-tight text-gray-900 mb-8">Order History</h1>
            <div className="bg-white p-8 rounded-2xl border border-sand shadow-card text-center">
              <div className="mb-4">
                <FiPackage className="w-16 h-16 mx-auto text-gray-400" />
              </div>
              <h2 className="text-2xl font-display font-normal tracking-tight mb-2">No orders yet</h2>
              <p className="text-gray-600 mb-6">You haven't placed any orders yet.</p>
              <Link
                to="/"
                className="bg-ink text-cream px-6 py-2 rounded-xl font-medium hover:bg-brand-500 hover:text-ink transition-colors"
              >
                Start Shopping
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="pt-24 min-h-screen bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-display font-normal tracking-tight text-gray-900 mb-8">Order History</h1>

          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order._id} className="bg-white rounded-2xl border border-sand shadow-card hover:shadow-card-hover overflow-hidden">
                {/* Order Header */}
                <div className="border-b border-sand p-6">
                  <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">Order placed</p>
                      <p className="font-medium">
                        {new Date(order.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">Order number</p>
                      <p className="font-medium">{order._id.substring(0, 8)}...</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">Total amount</p>
                      <p className="font-medium">EGP {order.totalOrderPrice.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(order.isDelivered ? 'delivered' : 'processing')}
                      <span className="font-medium">{order.isDelivered ? 'Delivered' : (order.isPaid ? 'Shipped' : 'Processing')}</span>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="p-6">
                  {order.cartItems.map((item) => {
                    const productId = item.product?._id;
                    const product = getProductDetail(productId);
                    
                    return (
                      <div key={item._id} className="flex items-center py-4">
                        <img
                          src={product?.images?.[0] || 'https://via.placeholder.com/150'}
                          alt={product?.name || 'Product'}
                          className="w-20 h-20 object-cover rounded"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/150';
                          }}
                        />
                        <div className="ml-6 flex-1">
                          <h3 className="font-medium text-gray-900">{product?.name || 'Product'}</h3>
                          <p className="text-gray-500">Quantity: {item.quantity}</p>
                          {item.leftEyePower && item.rightEyePower && (
                            <div className="text-gray-500 text-sm mt-1">
                              <span>Left: {item.leftEyePower} | Right: {item.rightEyePower}</span>
                            </div>
                          )}
                          <p className="text-gray-500">EGP {(product?.price || 0).toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Order Actions */}
                <div className="border-t border-sand p-6 bg-cream">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">Delivered to</p>
                      <p className="font-medium">
                        {order.shippingAddress?.details}, {order.shippingAddress?.city}
                      </p>
                    </div>
                    <Link
                      to={`/orders/track/${order._id}`}
                      className="bg-ink text-cream px-6 py-2 rounded-xl font-medium hover:bg-brand-500 hover:text-ink transition-colors"
                    >
                      Track Order
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderHistory; 